import { trechoDaSessao } from "./sessao";
/**
 * Integração entre os sistemas SERCAMP — navegação com login único (SSO via URL).
 * Os apps rodam localmente em portas diferentes; ao trocar de sistema levamos o
 * usuário logado pela URL (#sso=...) para não pedir login de novo.
 */

/** URL do Portal: localhost em dev; apex do dominio em producao (sem hardcode do dominio). */
function portalHost(): string {
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    const isLocal = h === "localhost" || h === "127.0.0.1" || h === "::1";
    if (!isLocal) {
      // O Portal fica no apex; este sistema esta num subdominio -> remove o 1o rotulo.
      const root = h.replace(/^www\./, "").split(".").slice(1).join(".") || h;
      return `${window.location.protocol}//${root}`;
    }
  }
  return "http://localhost:8080";
}

export const SYSTEMS = {
  portal: portalHost(),
  rh: "http://localhost:4001",
  dp: "http://localhost:4002",
  qualidade: "http://localhost:4003",
  planejamento: "http://localhost:4005",
} as const;

/** Identificador deste sistema no Portal (usado no controle de acesso). */
export const SYSTEM_ID = "qualidade";

/**
 * Login global do Admin Supremo. É o único acesso que SEMPRE é permitido e
 * SEMPRE é admin, mesmo sem cadastro local neste sistema. Comparar sempre em
 * minúsculas e com trim.
 */
export const ADMIN_SUPREMO_LOGIN = "admin@portalsercamp.com";

/** Usuário é o Admin Supremo? (comparação case-insensitive, com trim). */
export function isAdminSupremo(user: string | null | undefined): boolean {
  return (user ?? "").trim().toLowerCase() === ADMIN_SUPREMO_LOGIN;
}

/** Resultado da checagem de acesso junto ao Portal. */
export interface AcessoResult {
  allowed: boolean;
  adminSupremo: boolean;
  /** Acesso somente visualização (cadastro do Portal). */
  somenteLeitura: boolean;
  /** Abas liberadas neste sistema; null = todas. */
  abas: string[] | null;
}

/**
 * Pergunta ao Portal se <user> pode acessar este sistema.
 * GET {PORTAL_URL}/api/acesso?u=<usuario>&sistema=<SYSTEM_ID> -> { allowed, adminSupremo }.
 * Em caso de falha de rede o erro é propagado para o chamador tratar (fail-safe:
 * não liberar quando não dá para verificar).
 */
export async function checkAcesso(user: string): Promise<AcessoResult> {
  const url = `${SYSTEMS.portal}/api/acesso?u=${encodeURIComponent(user)}&sistema=${encodeURIComponent(SYSTEM_ID)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Portal respondeu ${res.status}`);
  const body = (await res.json()) as Partial<AcessoResult>;
  return {
    allowed: !!body.allowed,
    adminSupremo: !!body.adminSupremo,
    somenteLeitura: body.somenteLeitura === true,
    abas: body.somenteLeitura === true && Array.isArray(body.abas) ? body.abas.map(String) : null,
  };
}

/** Um aviso ativo publicado pelo Admin Cybersecurity no Portal. */
export interface AvisoSistema {
  id: number;
  tipo: "falha" | "manutencao" | "comunicado";
  titulo: string;
  mensagem: string;
  sistema_alvo: string | null;
  criado_em: string;
  expira_em: string | null;
}

/**
 * Busca os avisos ativos do Portal para este sistema (endpoint público,
 * sem autenticação, somente leitura).
 * GET {PORTAL_URL}/api/security/notificacoes/ativas?sistema=<SYSTEM_ID> -> AvisoSistema[].
 */
export async function fetchNotificacoesAtivas(): Promise<AvisoSistema[]> {
  const url = `${SYSTEMS.portal}/api/security/notificacoes/ativas?sistema=${encodeURIComponent(SYSTEM_ID)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Portal respondeu ${res.status}`);
  const body = await res.json();
  return Array.isArray(body) ? body : [];
}

/** Lê o usuário vindo do Portal/outro sistema pela URL (#sso=...). */
export function readSsoUser(): string | null {
  const m = window.location.hash.match(/(?:^|[#&])sso=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Remove o token SSO da URL após o login (deixa a barra de endereço limpa). */
export function clearSsoFromUrl() {
  if (window.location.hash.includes("sso=")) {
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

/** Navega para outro sistema levando o usuário logado (login único). */
export function goToSystem(url: string, user: string | null) {
  window.location.href = user ? `${url}/#sso=${encodeURIComponent(user)}${trechoDaSessao()}` : url;
}
